import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Calendar, Check, X, ArrowRight, ArrowLeft, Package, Plus, Music, Users, MapPin, Send, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WidgetAvailability() {
  const { userId } = useParams();
  const [searchParams] = useSearchParams();
  const theme = searchParams.get('theme') || 'dark';
  const isLight = theme === 'light';
  
  // Step management: 1 = date check, 2 = package selection, 3 = contact form
  const [step, setStep] = useState(1);
  
  // Step 1: Date check
  const [selectedDate, setSelectedDate] = useState('');
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  
  // Step 2: Package selection
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [company, setCompany] = useState({});
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  
  // Step 3: Contact form
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    event_type: '',
    venue: '',
    guest_count: '',
    notes: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Track analytics on mount
  useEffect(() => {
    const trackView = async () => {
      try {
        // Generate or get session ID
        let sessionId = sessionStorage.getItem('analytics_session');
        if (!sessionId) {
          sessionId = `sess_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          sessionStorage.setItem('analytics_session', sessionId);
        }

        await axios.post(`${API}/analytics/track/${userId}`, {
          event_type: 'widget_view',
          page: 'widget_availability',
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
          screen_size: `${window.innerWidth}x${window.innerHeight}`,
          session_id: sessionId
        });
      } catch (e) {
        // Silent fail for analytics
      }
    };
    
    if (userId) trackView();
  }, [userId]);

  // Send resize message to parent for iframe auto-height
  useEffect(() => {
    const sendHeight = () => {
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: 'djcrm-resize', widget: 'availability', height }, '*');
    };
    
    // Send initial height
    sendHeight();
    
    // Observe DOM changes and resize
    const observer = new ResizeObserver(sendHeight);
    observer.observe(document.body);
    
    // Also send on window resize
    window.addEventListener('resize', sendHeight);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', sendHeight);
    };
  }, [step, result, packages, selectedPackage, submitted]); // Re-run when content changes

  // Set body background color for proper iframe display
  useEffect(() => {
    document.body.style.backgroundColor = isLight ? '#ffffff' : '#0a0a0f';
    document.documentElement.style.backgroundColor = isLight ? '#ffffff' : '#0a0a0f';
    return () => {
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    };
  }, [isLight]);

  // Calculate total amount
  const calculateTotal = () => {
    let total = selectedPackage?.base_price || 0;
    selectedAddons.forEach(addonId => {
      const addon = addons.find(a => a.id === addonId);
      if (addon) total += addon.price || 0;
    });
    return total;
  };

  const checkAvailability = async () => {
    if (!selectedDate) return;
    
    setChecking(true);
    setResult(null);
    
    try {
      const response = await axios.post(`${API}/public/availability/${userId}`, {
        date: selectedDate
      });
      setResult(response.data);
      
      // If available, fetch packages
      if (response.data.available) {
        fetchPackages();
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      setResult({ error: true, message: 'Unable to check availability. Please try again.' });
    } finally {
      setChecking(false);
    }
  };

  const fetchPackages = async () => {
    setLoadingPackages(true);
    try {
      const response = await axios.get(`${API}/public/packages/${userId}`);
      setPackages(response.data.packages || []);
      setAddons(response.data.addons || []);
      setCompany(response.data.company || {});
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoadingPackages(false);
    }
  };

  const handlePackageSelect = (pkg) => {
    setSelectedPackage(pkg);
    setSelectedAddons([]);
  };

  const handleAddonToggle = (addonId) => {
    setSelectedAddons(prev => 
      prev.includes(addonId)
        ? prev.filter(id => id !== addonId)
        : [...prev, addonId]
    );
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Please fill in your name and email');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/event-request/${userId}`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        event_date: selectedDate,
        event_type: formData.event_type,
        venue: formData.venue,
        guest_count: formData.guest_count ? parseInt(formData.guest_count) : null,
        package_id: selectedPackage?.id,
        package_name: selectedPackage?.name,
        selected_addons: selectedAddons,
        total_amount: calculateTotal(),
        notes: formData.notes
      });
      
      // Track form submission
      try {
        const sessionId = sessionStorage.getItem('analytics_session');
        await axios.post(`${API}/analytics/track/${userId}`, {
          event_type: 'form_submit',
          page: 'widget_availability',
          referrer: document.referrer || null,
          session_id: sessionId
        });
      } catch (e) { /* Silent fail */ }
      
      setSubmitted(true);
      toast.success('Your request has been submitted!');
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  // Filter addons based on selected package
  const getAvailableAddons = () => {
    if (!selectedPackage) return [];
    return addons.filter(addon => {
      const packageList = addon.available_for_packages || [];
      // If no restrictions (empty list), available for all packages
      // If restrictions exist, check if selected package is in the list
      return packageList.length === 0 || packageList.includes(selectedPackage.id);
    });
  };

  // Step 1: Date Check
  if (step === 1) {
    return (
      <div className={`min-h-fit py-8 flex items-center justify-center p-4 ${isLight ? 'bg-white' : 'bg-obsidian'}`}>
        <div className="w-full max-w-md">
          <div className={`rounded-2xl p-8 border ${isLight ? 'bg-gray-50 border-gray-200 shadow-lg' : 'glass-card border-glass-border'}`}>
            <div className="text-center space-y-6">
              <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center ${isLight ? 'bg-cyan-100' : 'bg-neon-blue/20'}`}>
                <Calendar className={`w-8 h-8 ${isLight ? 'text-cyan-600' : 'text-neon-blue'}`} />
              </div>
              
              <div>
                <h1 className={`font-heading text-2xl font-bold ${isLight ? 'text-gray-900' : 'text-foreground'}`}>Check Our Availability</h1>
                <p className={`mt-2 ${isLight ? 'text-gray-600' : 'text-muted-foreground'}`}>Select your event date to see if we're available</p>
              </div>

              <div className="space-y-4">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setResult(null);
                  }}
                  min={getMinDate()}
                  className={`w-full rounded-xl px-4 py-3 focus:outline-none transition-colors ${
                    isLight 
                      ? 'bg-white border border-gray-300 text-gray-900 focus:border-cyan-500' 
                      : 'bg-muted/20 border border-glass-border text-foreground focus:border-neon-blue'
                  }`}
                  data-testid="availability-date-input"
                />

                <Button
                  onClick={checkAvailability}
                  disabled={!selectedDate || checking}
                  className={`w-full font-semibold py-6 ${
                    isLight 
                      ? 'bg-cyan-500 hover:bg-cyan-600 text-white' 
                      : 'bg-neon-blue hover:bg-neon-blue/80 text-black'
                  }`}
                  data-testid="check-availability-btn"
                >
                  {checking ? 'Checking...' : 'Check Availability'}
                </Button>
              </div>

              {result && !result.error && (
                <div className={`p-4 rounded-xl border ${
                  result.available 
                    ? 'bg-green-500/10 border-green-500/30' 
                    : 'bg-red-500/10 border-red-500/30'
                }`}>
                  <div className="flex items-center justify-center gap-2">
                    {result.available ? (
                      <>
                        <Check className="w-6 h-6 text-green-500" />
                        <span className="font-semibold text-green-500">We're Available!</span>
                      </>
                    ) : (
                      <>
                        <X className="w-6 h-6 text-red-500" />
                        <span className="font-semibold text-red-500">Date Not Available</span>
                      </>
                    )}
                  </div>
                  {result.available && (
                    <p className={`text-sm mt-2 ${isLight ? 'text-gray-600' : 'text-muted-foreground'}`}>
                      Great news! We're available on {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  )}
                  {!result.available && (
                    <p className={`text-sm mt-2 ${isLight ? 'text-gray-600' : 'text-muted-foreground'}`}>
                      Sorry, we're already booked on this date. Please try another date.
                    </p>
                  )}
                </div>
              )}

              {result?.error && (
                <div className="p-4 rounded-xl bg-yellow-500/10 border border-yellow-500/30">
                  <p className="text-sm text-yellow-500">{result.message}</p>
                </div>
              )}

              {result?.available && (
                <Button
                  className={`w-full font-semibold ${
                    isLight 
                      ? 'bg-purple-500 hover:bg-purple-600 text-white' 
                      : 'bg-neon-purple hover:bg-neon-purple/80 text-white'
                  }`}
                  onClick={() => setStep(2)}
                  data-testid="proceed-to-packages"
                >
                  View Our Packages
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </div>
          
          <p className={`text-center text-xs mt-4 ${isLight ? 'text-gray-400' : 'text-muted-foreground'}`}>
            Powered by DJ CRM
          </p>
        </div>
      </div>
    );
  }

  // Step 2: Package Selection
  if (step === 2) {
    return (
      <div className={`min-h-fit py-8 p-4 ${isLight ? 'bg-white' : 'bg-obsidian'}`}>
        <div className="max-w-4xl mx-auto">
          <div className={`rounded-2xl p-6 border ${isLight ? 'bg-gray-50 border-gray-200 shadow-lg' : 'glass-card border-glass-border'}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <Button
                variant="ghost"
                onClick={() => setStep(1)}
                className={isLight ? 'text-gray-600 hover:text-gray-900' : 'text-muted-foreground hover:text-foreground'}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="text-center">
                <h1 className={`font-heading text-xl font-bold ${isLight ? 'text-gray-900' : 'text-foreground'}`}>Choose Your Package</h1>
                <p className={`text-sm ${isLight ? 'text-gray-600' : 'text-muted-foreground'}`}>
                  Event Date: {new Date(selectedDate + 'T00:00:00').toLocaleDateString()}
                </p>
              </div>
              <div className="w-20"></div>
            </div>

            {loadingPackages ? (
              <div className="text-center py-12">
                <Loader2 className={`w-8 h-8 animate-spin mx-auto ${isLight ? 'text-cyan-600' : 'text-neon-blue'}`} />
                <p className={isLight ? 'text-gray-600 mt-2' : 'text-muted-foreground mt-2'}>Loading packages...</p>
              </div>
            ) : packages.length === 0 ? (
              <div className="text-center py-12">
                <Package className={`w-12 h-12 mx-auto mb-4 ${isLight ? 'text-gray-400' : 'text-muted-foreground'}`} />
                <p className={isLight ? 'text-gray-600' : 'text-muted-foreground'}>No packages available at this time.</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => setStep(3)}
                >
                  Continue Without Package
                </Button>
              </div>
            ) : (
              <>
                {/* Packages Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                  {packages.map((pkg) => (
                    <div
                      key={pkg.id}
                      onClick={() => handlePackageSelect(pkg)}
                      className={`rounded-xl border cursor-pointer transition-all hover:scale-[1.02] ${
                        selectedPackage?.id === pkg.id
                          ? isLight 
                            ? 'border-cyan-500 bg-cyan-50 ring-2 ring-cyan-500'
                            : 'border-neon-blue bg-neon-blue/10 ring-2 ring-neon-blue'
                          : isLight
                            ? 'border-gray-200 bg-white hover:border-cyan-300'
                            : 'border-glass-border bg-card hover:border-neon-blue/50'
                      }`}
                      data-testid={`package-card-${pkg.id}`}
                    >
                      {/* Package Image */}
                      {pkg.image_url ? (
                        <div className="h-40 rounded-t-xl overflow-hidden">
                          <img 
                            src={pkg.image_url} 
                            alt={pkg.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className={`h-40 rounded-t-xl flex items-center justify-center ${
                          isLight 
                            ? 'bg-gradient-to-br from-cyan-100 to-purple-100' 
                            : 'bg-gradient-to-br from-neon-blue/20 to-neon-purple/20'
                        }`}>
                          <Music className={isLight ? 'w-12 h-12 text-gray-400' : 'w-12 h-12 text-muted-foreground'} />
                        </div>
                      )}
                      
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <h3 className={`font-heading font-semibold ${isLight ? 'text-gray-900' : 'text-foreground'}`}>{pkg.name}</h3>
                          {selectedPackage?.id === pkg.id && (
                            <Check className={isLight ? 'w-5 h-5 text-cyan-600' : 'w-5 h-5 text-neon-blue'} />
                          )}
                        </div>
                        <p className={`text-2xl font-bold mb-2 ${isLight ? 'text-cyan-600' : 'text-neon-blue'}`}>
                          {formatCurrency(pkg.base_price)}
                        </p>
                        {pkg.description && (
                          <p className={`text-sm line-clamp-2 ${isLight ? 'text-gray-600' : 'text-muted-foreground'}`}>{pkg.description}</p>
                        )}
                        {pkg.includes && pkg.includes.length > 0 && (
                          <ul className="mt-3 space-y-1">
                            {pkg.includes.slice(0, 3).map((item, idx) => (
                              <li key={idx} className={`text-xs flex items-center gap-1 ${isLight ? 'text-gray-600' : 'text-muted-foreground'}`}>
                                <Check className="w-3 h-3 text-green-500" />
                                {item}
                              </li>
                            ))}
                            {pkg.includes.length > 3 && (
                              <li className={`text-xs ${isLight ? 'text-cyan-600' : 'text-neon-blue'}`}>+{pkg.includes.length - 3} more</li>
                            )}
                          </ul>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Add-ons Section */}
                {selectedPackage && getAvailableAddons().length > 0 && (
                  <div className={`border-t pt-6 ${isLight ? 'border-gray-200' : 'border-glass-border'}`}>
                    <h2 className={`font-heading text-lg font-semibold mb-4 flex items-center gap-2 ${isLight ? 'text-gray-900' : 'text-foreground'}`}>
                      <Plus className={isLight ? 'w-5 h-5 text-purple-600' : 'w-5 h-5 text-neon-purple'} />
                      Enhance Your Experience with Add-Ons
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {getAvailableAddons().map((addon) => (
                        <div
                          key={addon.id}
                          onClick={() => handleAddonToggle(addon.id)}
                          className={`p-4 rounded-lg border cursor-pointer transition-all flex items-start gap-3 ${
                            selectedAddons.includes(addon.id)
                              ? isLight
                                ? 'border-purple-500 bg-purple-50'
                                : 'border-neon-purple bg-neon-purple/10'
                              : isLight
                                ? 'border-gray-200 bg-white hover:border-purple-300'
                                : 'border-glass-border bg-card hover:border-neon-purple/50'
                          }`}
                          data-testid={`addon-card-${addon.id}`}
                        >
                          <Checkbox
                            checked={selectedAddons.includes(addon.id)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <span className={`font-medium ${isLight ? 'text-gray-900' : 'text-foreground'}`}>{addon.name}</span>
                              <span className={`font-semibold ${isLight ? 'text-purple-600' : 'text-neon-purple'}`}>+{formatCurrency(addon.price)}</span>
                            </div>
                            {addon.description && (
                              <p className={`text-sm mt-1 ${isLight ? 'text-gray-600' : 'text-muted-foreground'}`}>{addon.description}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Summary & Continue */}
                <div className={`mt-6 pt-6 border-t ${isLight ? 'border-gray-200' : 'border-glass-border'}`}>
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className={`text-sm ${isLight ? 'text-gray-600' : 'text-muted-foreground'}`}>Estimated Total</p>
                      <p className={`text-2xl font-bold ${isLight ? 'text-gray-900' : 'text-foreground'}`}>{formatCurrency(calculateTotal())}</p>
                    </div>
                    <Button
                      onClick={() => setStep(3)}
                      className={`font-semibold px-8 ${
                        isLight 
                          ? 'bg-cyan-500 hover:bg-cyan-600 text-white' 
                          : 'bg-neon-blue hover:bg-neon-blue/80 text-black'
                      }`}
                      disabled={!selectedPackage}
                      data-testid="proceed-to-contact"
                    >
                      Continue
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                  {!selectedPackage && (
                    <p className="text-sm text-yellow-500">Please select a package to continue</p>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Step 3: Contact Form
  if (step === 3) {
    if (submitted) {
      return (
        <div className={`min-h-fit py-8 flex items-center justify-center p-4 ${isLight ? 'bg-white' : 'bg-obsidian'}`}>
          <div className="w-full max-w-md">
            <div className={`rounded-2xl p-8 border text-center ${isLight ? 'bg-gray-50 border-gray-200 shadow-lg' : 'glass-card border-glass-border'}`}>
              <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center mb-6">
                <Check className="w-8 h-8 text-green-500" />
              </div>
              <h1 className={`font-heading text-2xl font-bold mb-2 ${isLight ? 'text-gray-900' : 'text-foreground'}`}>Request Submitted!</h1>
              <p className={`mb-6 ${isLight ? 'text-gray-600' : 'text-muted-foreground'}`}>
                Thank you for your inquiry. We'll review your request and get back to you soon.
              </p>
              <div className={`p-4 rounded-lg border text-left space-y-2 ${isLight ? 'bg-white border-gray-200' : 'bg-card border-glass-border'}`}>
                <p className="text-sm"><span className={isLight ? 'text-gray-500' : 'text-muted-foreground'}>Date:</span> <span className={isLight ? 'text-gray-900' : 'text-foreground'}>{new Date(selectedDate + 'T00:00:00').toLocaleDateString()}</span></p>
                {selectedPackage && (
                  <p className="text-sm"><span className={isLight ? 'text-gray-500' : 'text-muted-foreground'}>Package:</span> <span className={isLight ? 'text-gray-900' : 'text-foreground'}>{selectedPackage.name}</span></p>
                )}
                <p className="text-sm"><span className={isLight ? 'text-gray-500' : 'text-muted-foreground'}>Total:</span> <span className={`font-semibold ${isLight ? 'text-cyan-600' : 'text-neon-blue'}`}>{formatCurrency(calculateTotal())}</span></p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className={`min-h-fit py-8 p-4 ${isLight ? 'bg-white' : 'bg-obsidian'}`}>
        <div className="max-w-2xl mx-auto">
          <div className={`rounded-2xl p-6 border ${isLight ? 'bg-gray-50 border-gray-200 shadow-lg' : 'glass-card border-glass-border'}`}>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <Button
                variant="ghost"
                onClick={() => setStep(2)}
                className={isLight ? 'text-gray-600 hover:text-gray-900' : 'text-muted-foreground hover:text-foreground'}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
              <div className="text-center">
                <h1 className={`font-heading text-xl font-bold ${isLight ? 'text-gray-900' : 'text-foreground'}`}>Complete Your Request</h1>
              </div>
              <div className="w-20"></div>
            </div>

            {/* Summary */}
            <div className={`p-4 rounded-lg border mb-6 ${isLight ? 'bg-white border-gray-200' : 'bg-card border-glass-border'}`}>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <p className={`text-xs ${isLight ? 'text-gray-500' : 'text-muted-foreground'}`}>Date</p>
                  <p className={`font-semibold ${isLight ? 'text-gray-900' : 'text-foreground'}`}>{new Date(selectedDate + 'T00:00:00').toLocaleDateString()}</p>
                </div>
                <div>
                  <p className={`text-xs ${isLight ? 'text-gray-500' : 'text-muted-foreground'}`}>Package</p>
                  <p className={`font-semibold ${isLight ? 'text-gray-900' : 'text-foreground'}`}>{selectedPackage?.name || 'Custom'}</p>
                </div>
                <div>
                  <p className={`text-xs ${isLight ? 'text-gray-500' : 'text-muted-foreground'}`}>Estimated Total</p>
                  <p className={`font-semibold ${isLight ? 'text-cyan-600' : 'text-neon-blue'}`}>{formatCurrency(calculateTotal())}</p>
                </div>
              </div>
              {selectedAddons.length > 0 && (
                <div className={`mt-3 pt-3 border-t ${isLight ? 'border-gray-200' : 'border-glass-border'}`}>
                  <p className={`text-xs mb-1 ${isLight ? 'text-gray-500' : 'text-muted-foreground'}`}>Add-Ons:</p>
                  <div className="flex flex-wrap gap-1">
                    {selectedAddons.map(addonId => {
                      const addon = addons.find(a => a.id === addonId);
                      return addon ? (
                        <span key={addonId} className={`text-xs px-2 py-1 rounded ${isLight ? 'bg-purple-100 text-purple-700' : 'bg-neon-purple/20 text-neon-purple'}`}>
                          {addon.name}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </div>

            {/* Contact Form */}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isLight ? 'text-gray-900' : 'text-foreground'}>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Your name"
                    className={isLight ? 'bg-white border-gray-300' : 'bg-muted border-glass-border'}
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isLight ? 'text-gray-900' : 'text-foreground'}>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="your@email.com"
                    className={isLight ? 'bg-white border-gray-300' : 'bg-muted border-glass-border'}
                    data-testid="contact-email-input"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={isLight ? 'text-gray-900' : 'text-foreground'}>Phone</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="(555) 123-4567"
                    className={isLight ? 'bg-white border-gray-300' : 'bg-muted border-glass-border'}
                    data-testid="contact-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={isLight ? 'text-gray-900' : 'text-foreground'}>Event Type</Label>
                  <Input
                    value={formData.event_type}
                    onChange={(e) => setFormData({ ...formData, event_type: e.target.value })}
                    placeholder="Wedding, Corporate, etc."
                    className={isLight ? 'bg-white border-gray-300' : 'bg-muted border-glass-border'}
                    data-testid="contact-event-type-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className={`flex items-center gap-1 ${isLight ? 'text-gray-900' : 'text-foreground'}`}>
                    <MapPin className="w-3 h-3" /> Venue
                  </Label>
                  <Input
                    value={formData.venue}
                    onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
                    placeholder="Venue name or location"
                    className={isLight ? 'bg-white border-gray-300' : 'bg-muted border-glass-border'}
                    data-testid="contact-venue-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className={`flex items-center gap-1 ${isLight ? 'text-gray-900' : 'text-foreground'}`}>
                    <Users className="w-3 h-3" /> Guest Count
                  </Label>
                  <Input
                    type="number"
                    value={formData.guest_count}
                    onChange={(e) => setFormData({ ...formData, guest_count: e.target.value })}
                    placeholder="150"
                    className={isLight ? 'bg-white border-gray-300' : 'bg-muted border-glass-border'}
                    data-testid="contact-guests-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label className={isLight ? 'text-gray-900' : 'text-foreground'}>Additional Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Tell us more about your event..."
                  className={isLight ? 'bg-white border-gray-300' : 'bg-muted border-glass-border'}
                  rows={3}
                  data-testid="contact-notes-input"
                />
              </div>

              <Button
                onClick={handleSubmit}
                disabled={submitting || !formData.name || !formData.email}
                className={`w-full font-semibold py-6 ${
                  isLight 
                    ? 'bg-cyan-500 hover:bg-cyan-600 text-white' 
                    : 'bg-neon-blue hover:bg-neon-blue/80 text-black'
                }`}
                data-testid="submit-request-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Request
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
